import { inject, Injectable, OnDestroy } from '@angular/core';
import { Title } from '@angular/platform-browser';
import { RouterStateSnapshot, TitleStrategy } from '@angular/router';
import { TranslocoService } from '@jsverse/transloco';
import { Subscription } from 'rxjs';

/**
 * Custom TitleStrategy that composes document.title as:
 *   "{app name} · {page title}"
 * both values come from Transloco translations.
 * Title is refreshed automatically on language change.
 */
@Injectable({ providedIn: 'root' })
export class TranslocoTitleStrategy extends TitleStrategy implements OnDestroy {
  private readonly titleService = inject(Title);
  private readonly transloco = inject(TranslocoService);
  private titleSub: Subscription | null = null;

  override updateTitle(snapshot: RouterStateSnapshot): void {
    this.applyTitle(snapshot);
  }

  private applyTitle(snapshot: RouterStateSnapshot): void {
    const pageKey = this.buildTitle(snapshot);
    const keys = pageKey ? ['header.title', pageKey] : ['header.title'];

    // selectTranslate waits for the active language to be loaded before
    // emitting (fixes empty titles on first startup) and re-emits on
    // every language change, so no manual langChanges$ subscription is needed.
    this.titleSub?.unsubscribe();
    this.titleSub = this.transloco.selectTranslate(keys).subscribe((values: string[]) => {
      const appName = values[0];

      if (!pageKey) {
        this.titleService.setTitle(appName);
        return;
      }

      this.titleService.setTitle(`${appName} · ${values[1]}`);
    });
  }

  ngOnDestroy(): void {
    this.titleSub?.unsubscribe();
  }
}
